import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const HANUMAN_URL = 'https://customer-assets-39nsmqrw.emergentagent.net/job_ranjeet-services-1/artifacts/on4ccl7b_1000469426.jpg.png';

/* ─── Canvas starfield ─── */
const StarCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); let id;
    c.width = window.innerWidth; c.height = window.innerHeight;
    const stars = Array.from({ length: 150 }, () => ({
      x: Math.random() * c.width, y: Math.random() * c.height,
      r: Math.random() * 2.2 + 0.3, ph: Math.random() * Math.PI * 2,
      sp: Math.random() * 0.025 + 0.008, col: Math.random() > 0.8 ? '#FF9933' : '#FFD700',
    }));
    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height); t += 0.016;
      stars.forEach(s => {
        const op = 0.15 + 0.85 * Math.abs(Math.sin(t * s.sp + s.ph));
        ctx.globalAlpha = op; ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.col; ctx.fill();
        if (s.r > 1.4) {
          const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 4);
          g.addColorStop(0, `rgba(255,215,0,${op * 0.35})`); g.addColorStop(1, 'rgba(255,215,0,0)');
          ctx.beginPath(); ctx.arc(s.x, s.y, s.r * 4, 0, Math.PI * 2); ctx.fillStyle = g; ctx.fill();
        }
        ctx.globalAlpha = 1;
      });
      id = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(id);
  }, []);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
};

/* ─── Falling petals ─── */
const Petals = () => {
  const petals = useMemo(() => Array.from({ length: 14 }, (_, i) => ({
    id: i, left: `${4 + i * 7}%`, delay: `${i * 0.6}s`,
    duration: `${7 + (i % 4)}s`, char: ['🌸', '🌺', '🌼', '🌹'][i % 4],
  })), []);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {petals.map(p => <span key={p.id} className="petal" style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration }}>{p.char}</span>)}
    </div>
  );
};

/* ─── Diamond sparkles around Hanuman image ─── */
const DiamondSparkles = () => {
  const sparkles = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const angle = (i * 30) * Math.PI / 180;
    const r = 155 + (i % 3) * 20;
    return { id: i, x: 50 + r * Math.cos(angle) / 3.5, y: 50 + r * Math.sin(angle) / 3.5, delay: `${i * 0.25}s`, size: 6 + (i % 3) * 3 };
  }), []);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {sparkles.map(s => (
        <div key={s.id} style={{
          position: 'absolute', left: `${s.x}%`, top: `${s.y}%`,
          width: s.size, height: s.size,
          background: 'radial-gradient(circle, #FFF3B0, #FFD700)',
          clipPath: 'polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)',
          animationName: 'sparkle-anim', animationDuration: `${1.5 + (s.id % 3) * 0.4}s`,
          animationTimingFunction: 'ease-in-out', animationIterationCount: 'infinite',
          animationDelay: s.delay,
          filter: 'drop-shadow(0 0 4px #FFD700)',
        }} />
      ))}
    </div>
  );
};

/* ─── Animated Hanuman image with glow/flash/rotate rings ─── */
const HanumanDivine = ({ visible }) => {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.3 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, type: 'spring', stiffness: 80, damping: 14 }}
          style={{ position: 'relative', width: 260, height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {/* Outer spinning ring - slow */}
          <div style={{
            position: 'absolute', inset: -22,
            borderRadius: '50%',
            border: '2px dashed rgba(255,215,0,0.35)',
            animation: 'spin-slow 25s linear infinite',
          }} />

          {/* Middle spinning ring - medium, reverse */}
          <div style={{
            position: 'absolute', inset: -12,
            borderRadius: '50%',
            border: '1.5px solid rgba(255,153,0,0.5)',
            animation: 'spin-slow 15s linear infinite reverse',
          }} />

          {/* 8 golden rays behind image */}
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} style={{
              position: 'absolute', left: '50%', top: '50%',
              width: '2px', height: '140px',
              background: `linear-gradient(to bottom, rgba(255,215,0,${0.5 - i * 0.02}), transparent)`,
              transformOrigin: 'top center',
              transform: `rotate(${i * 45}deg) translateX(-50%)`,
              animation: `ray-sweep ${2 + i * 0.3}s ease-in-out infinite alternate`,
            }} />
          ))}

          {/* Golden glow halo behind image */}
          <div style={{
            position: 'absolute', inset: -8,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,153,0,0.25) 0%, rgba(255,215,0,0.12) 40%, transparent 70%)',
            animation: 'divine-pulse 2.5s ease-in-out infinite',
          }} />

          {/* The actual Hanuman Ji image */}
          <div style={{
            width: 240, height: 240, borderRadius: '50%', overflow: 'hidden',
            border: '3px solid #FFD700',
            boxShadow: '0 0 30px rgba(255,215,0,0.8), 0 0 60px rgba(255,153,0,0.5), inset 0 0 20px rgba(255,215,0,0.1)',
            position: 'relative', zIndex: 2,
            animation: 'hanuman-divine-flash 4s ease-in-out infinite',
          }}>
            <img
              src={HANUMAN_URL}
              alt="Hanuman Ji"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 10%', display: 'block' }}
            />
          </div>

          {/* Diamond sparkles */}
          <DiamondSparkles />

          {/* Inner glow ring */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 30%, rgba(255,243,176,0.08), transparent 60%)',
            pointerEvents: 'none', zIndex: 3,
          }} />

          <style>{`
            @keyframes hanuman-divine-flash {
              0%, 80%, 100% {
                box-shadow: 0 0 25px rgba(255,215,0,0.7), 0 0 55px rgba(255,153,0,0.4);
                filter: brightness(1);
              }
              85% {
                box-shadow: 0 0 50px rgba(255,243,176,0.9), 0 0 100px rgba(255,215,0,0.8), 0 0 160px rgba(255,153,0,0.5);
                filter: brightness(1.35);
              }
              87% {
                box-shadow: 0 0 20px rgba(255,215,0,0.5), 0 0 40px rgba(255,153,0,0.3);
                filter: brightness(0.95);
              }
              90% {
                box-shadow: 0 0 60px rgba(255,243,176,1), 0 0 120px rgba(255,215,0,0.9), 0 0 200px rgba(255,153,0,0.6);
                filter: brightness(1.6);
              }
              95% {
                box-shadow: 0 0 30px rgba(255,215,0,0.8), 0 0 70px rgba(255,153,0,0.5);
                filter: brightness(1.1);
              }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─── Voice / sound ─── */
const voiceText = `जय श्री राम! श्री रणजीत सर्विसेस में आपका हार्दिक स्वागत है। हर खुशी को दीजिए डिजिटल पहचान। बुद्धिर्बलं यशो धैर्यं निर्भयत्वमरोगता। अजाड्यं वाक्पटुत्वं च हनुमत्स्मरणाद्भवेत्॥ अर्थात् — हनुमान जी का स्मरण करने से बुद्धि, बल, यश, साहस, निर्भयता, स्वास्थ्य एवं वाणी की शक्ति प्राप्त होती है।`;

const playVoice = () => {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(voiceText);
  utt.lang = 'hi-IN'; utt.rate = 0.78; utt.pitch = 0.85; utt.volume = 1;
  window.speechSynthesis.speak(utt);
};

const playBell = () => {
  try {
    const a = new (window.AudioContext || window.webkitAudioContext)();
    const osc = a.createOscillator(); const g = a.createGain();
    osc.connect(g); g.connect(a.destination);
    osc.frequency.setValueAtTime(880, a.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, a.currentTime + 1.5);
    g.gain.setValueAtTime(0.15, a.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, a.currentTime + 2);
    osc.start(a.currentTime); osc.stop(a.currentTime + 2);
  } catch (_) {}
};

/* ─── Flashing text component ─── */
const FlashText = ({ children, color = '#FFD700', style = {} }) => (
  <span style={{ ...style, color, animation: 'glow-text-flash 1.8s ease-in-out infinite', display: 'inline-block' }}>
    {children}
  </span>
);

/* ─── Main SplashScreen ─── */
export default function SplashScreen({ onComplete }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 300),
      setTimeout(() => setPhase(2), 900),
      setTimeout(() => setPhase(3), 1800),
      setTimeout(() => setPhase(4), 2600),
      setTimeout(() => setPhase(5), 3400),
      setTimeout(() => setPhase(6), 4200),
      setTimeout(() => setPhase(7), 5200),
    ];
    const auto = setTimeout(() => onComplete(), 12000);
    return () => { timers.forEach(clearTimeout); clearTimeout(auto); };
  }, [onComplete]);

  const handleEnter = () => { window.speechSynthesis?.cancel(); onComplete(); };
  const handleVoice = () => { playBell(); setTimeout(playVoice, 600); };

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      data-testid="splash-screen"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999, overflow: 'hidden',
        background: 'radial-gradient(ellipse at 50% 30%, #1a2d5a 0%, #0A1128 55%, #050c1f 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* Scan lines overlay */}
      <div className="scan-lines" style={{ position: 'absolute', inset: 0, zIndex: 1 }} />

      {/* Background stars + petals */}
      <AnimatePresence>
        {phase >= 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }}
            style={{ position: 'absolute', inset: 0 }}>
            <StarCanvas />
            <Petals />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Temple golden rays */}
      {phase >= 1 && <div className="temple-light" style={{ position: 'absolute', inset: 0, zIndex: 2 }} />}

      {/* Radial golden center glow */}
      {phase >= 2 && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: '500px', height: '500px', borderRadius: '50%', zIndex: 2, pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(255,153,0,0.18) 0%, rgba(255,215,0,0.08) 35%, transparent 65%)',
          animation: 'divine-pulse 3s ease-in-out infinite',
        }} />
      )}

      {/* ─── Main content ─── */}
      <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '16px', maxWidth: '720px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>

        {/* Hanuman Ji Image */}
        <div style={{ marginBottom: '20px' }}>
          <HanumanDivine visible={phase >= 2} />
        </div>

        {/* Jai Shri Ram */}
        <AnimatePresence>
          {phase >= 3 && (
            <motion.p
              initial={{ opacity: 0, letterSpacing: '10px' }}
              animate={{ opacity: 1, letterSpacing: '5px' }}
              transition={{ duration: 0.8 }}
              className="hindi"
              style={{ fontSize: 'clamp(16px,3.5vw,26px)', fontWeight: 700, margin: '0 0 10px', color: '#FFD700' }}>
              <FlashText color="#FFD700">🚩 जय श्री राम 🚩</FlashText>
            </motion.p>
          )}
        </AnimatePresence>

        {/* Hindi firm name - BIG flash */}
        <AnimatePresence>
          {phase >= 4 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.7, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, type: 'spring', stiffness: 90 }}>
              <h1 className="hindi"
                style={{
                  fontFamily: '"Yatra One", cursive',
                  fontSize: 'clamp(28px,6.5vw,58px)',
                  margin: '0 0 4px', lineHeight: 1.1,
                  background: 'linear-gradient(90deg, #B8960C 0%, #FFD700 25%, #FFF3B0 50%, #FFD700 75%, #B8960C 100%)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text', backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'shimmer 2.5s linear infinite',
                  textShadow: 'none',
                  filter: 'drop-shadow(0 0 8px rgba(255,215,0,0.6))',
                }}>
                श्री रणजीत सर्विसेस
              </h1>
              <p style={{
                fontFamily: '"Cinzel Decorative", serif',
                fontSize: 'clamp(9px,2vw,15px)', color: '#FF9933',
                letterSpacing: '4px', margin: '0 0 16px',
                animation: 'glow-text 2s ease-in-out infinite',
              }}>
                SHRI RANJEET SERVICES
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sanskrit Shlok */}
        <AnimatePresence>
          {phase >= 5 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{
                background: 'rgba(10,17,40,0.75)', backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,215,0,0.3)', borderRadius: '14px',
                padding: '14px 22px', maxWidth: '560px', width: '100%', marginBottom: '10px',
                boxShadow: '0 0 20px rgba(255,215,0,0.1)',
              }}>
              <p className="hindi" style={{ fontSize: 'clamp(11px,1.8vw,15px)', color: '#FFF3B0', lineHeight: 1.9, margin: 0, fontStyle: 'italic', animation: 'glow-text 3s ease-in-out infinite' }}>
                बुद्धिर्बलं यशो धैर्यं निर्भयत्वमरोगता।<br />
                अजाड्यं वाक्पटुत्वं च हनुमत्स्मरणाद्भवेत्॥
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hindi Meaning */}
        <AnimatePresence>
          {phase >= 6 && (
            <motion.p
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="hindi-body"
              style={{ fontSize: 'clamp(10px,1.6vw,13px)', color: '#FF9933', lineHeight: 1.7, maxWidth: '520px', margin: '0 0 18px' }}>
              <span style={{ color: '#FFD700', fontWeight: 700 }}>अर्थात्</span> — हनुमान जी का स्मरण करने से बुद्धि, बल, यश, साहस,
              निर्भयता, स्वास्थ्य एवं वाणी की शक्ति प्राप्त होती है।
            </motion.p>
          )}
        </AnimatePresence>

        {/* Tagline flash */}
        <AnimatePresence>
          {phase >= 6 && (
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="hindi-body"
              style={{ fontSize: 'clamp(12px,2vw,16px)', margin: '0 0 16px', fontFamily: 'Khand, sans-serif' }}>
              <FlashText color="#FF9933">✦ हर खुशी को दीजिए डिजिटल पहचान ✦</FlashText>
            </motion.p>
          )}
        </AnimatePresence>

        {/* Action buttons */}
        <AnimatePresence>
          {phase >= 7 && (
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button data-testid="splash-enter-btn" onClick={handleEnter}
                style={{
                  padding: '13px 34px', borderRadius: '50px', fontSize: '15px',
                  fontFamily: '"Cinzel Decorative", serif', letterSpacing: '1px', border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #B8960C, #FFD700, #FF9933)',
                  color: '#0A1128', fontWeight: 700,
                  boxShadow: '0 0 20px rgba(255,215,0,0.5)',
                  animation: 'divine-pulse 2s ease-in-out infinite',
                }}>
                🙏 प्रवेश करें
              </button>
              <button data-testid="splash-voice-btn" onClick={handleVoice}
                style={{
                  padding: '13px 24px', borderRadius: '50px', fontSize: '14px',
                  background: 'rgba(19,32,74,0.9)', border: '1px solid rgba(255,153,0,0.5)',
                  color: '#FF9933', cursor: 'pointer', fontFamily: 'Khand, sans-serif', letterSpacing: '1px',
                }}>
                <i className="fa-solid fa-volume-high" style={{ marginRight: '8px' }} />
                आवाज़ सुनें
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Skip button */}
      {phase >= 3 && (
        <button data-testid="splash-skip-btn" onClick={handleEnter}
          style={{
            position: 'absolute', top: '18px', right: '18px', zIndex: 20,
            background: 'rgba(19,32,74,0.7)', border: '1px solid rgba(255,215,0,0.25)',
            color: 'rgba(255,243,176,0.65)', padding: '6px 14px', borderRadius: '20px',
            fontSize: '12px', cursor: 'pointer', fontFamily: 'Khand, sans-serif',
          }}>
          छोड़ें ✕
        </button>
      )}
    </motion.div>
  );
}
